/**
 * Your bookmarks in the space on screen (items, conversations, single comments, changes in the history): loaded once by
 * the shell, so any page can show whether something is bookmarked and toggle it. Yours alone: nobody else sees them.
 */
import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api } from "../api/client.ts";
import type { Bookmark, BookmarkKind, BookmarkTarget } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { commentAnchor, conversationPath, type Topic } from "./conversation.ts";
import { spaceApi } from "./paths.ts";
import { asApiError, useResource } from "./resource.ts";
import { toast } from "./toast.ts";

/** The kinds, in the order the Bookmarks page groups them. */
const BOOKMARK_KINDS: readonly BookmarkKind[] = ["item", "conversation", "comment", "commit"];

/** What a bookmark keeps, as one string: its kind and the id it names (`space` for the space's channel). */
export const bookmarkKey = (b: BookmarkTarget): string =>
  `${b.kind}:${b.kind === "comment" ? b.comment : (b.item ?? b.rule ?? b.entity ?? b.commit ?? "space")}`;

/** A comment as a bookmark names it. */
export const commentTarget = (comment: string): BookmarkTarget => ({ kind: "comment", comment });

/** A conversation as a bookmark names it. */
export const conversationTarget = (topic: Topic): BookmarkTarget => ({
  kind: "conversation",
  ...(topic.item ? { item: topic.item } : {}),
  ...(topic.rule ? { rule: topic.rule } : {}),
  ...(topic.entity ? { entity: topic.entity } : {}),
  ...(topic.commit ? { commit: topic.commit } : {}),
});

/** What a bookmark's conversation (or its item, or change) is about. */
export function aboutOf(b: BookmarkTarget): "item" | "rule" | "entity" | "commit" | "space" {
  if (b.item) {
    return "item";
  }
  if (b.rule) {
    return "rule";
  }
  if (b.entity) {
    return "entity";
  }
  return b.commit ? "commit" : "space";
}

/** What a bookmark is called: its title, or what stands for it (a change's id, the space's channel). */
export function bookmarkName(b: Bookmark): string {
  if (b.title) {
    return b.title;
  }
  if (b.kind === "commit") {
    return t("history.commit", { id: b.commit ?? "" });
  }
  return b.kind === "conversation" && aboutOf(b) === "space" ? t("bookmarks.channel") : t("common.untitled");
}

/** Where a bookmark goes in the space (below `/s/:space`): a comment's, to it in its conversation. */
export function bookmarkPath(b: BookmarkTarget): string {
  switch (b.kind) {
    case "item":
      return `/items/${b.item}`;
    case "commit":
      return `/history/${b.commit}`;
    case "comment":
      return `${conversationPath(b)}#${commentAnchor(b.comment ?? "")}`;
    default:
      return conversationPath(b);
  }
}

/** Bookmarks by kind (in `BOOKMARK_KINDS`' order, kinds without any left out), each as listed: newest first. */
export function groupBookmarks(list: readonly Bookmark[]): { kind: BookmarkKind; list: Bookmark[] }[] {
  return BOOKMARK_KINDS.map((kind) => ({ kind, list: list.filter((b) => b.kind === kind) })).filter(
    (g) => g.list.length > 0,
  );
}

/** The bookmarks of the space on screen, by key; none until loaded. */
const marks = signal<{ space: string; keys: ReadonlySet<string> } | null>(null);
/** The same bookmarks as listed (titles and all), as last loaded. */
const listed = signal<{ space: string; list: readonly Bookmark[] } | null>(null);
/**
 * Toggles saved here so far: a load asked for before the latest one may not have it, so it's not shown (the load after
 * it is). Bumping it also loads them again.
 */
const toggled = signal(0);
/** Toggles being saved now: what a load brings meanwhile may be from before them. */
let saving = 0;

/** Keeps the space's bookmarks loaded: on opening it, after each toggle, and on live updates (another tab's). */
export function useBookmarksLoader(spaceId: string): void {
  const at = toggled.value;
  const r = useResource(`bookmark-keys:${spaceId}:${at}`, async (s) => ({
    at,
    list: await api.get<Bookmark[]>(spaceApi({ id: spaceId }, "/bookmarks"), undefined, s),
  }));
  useEffect(() => {
    if (r.data && r.data.at === toggled.value && saving === 0) {
      marks.value = { space: spaceId, keys: new Set(r.data.list.map(bookmarkKey)) };
      listed.value = { space: spaceId, list: r.data.list };
    }
  }, [r.data, spaceId]);
}

/** Your bookmarks in `space` as last loaded (none until they are), those still bookmarked. */
export function bookmarksIn(space: string): readonly Bookmark[] {
  const l = listed.value;
  return l?.space === space ? l.list.filter((b) => isBookmarked(space, b)) : [];
}

/** Whether `b` is bookmarked in `space` (as far as is known: not until they're loaded). */
export function isBookmarked(space: string, b: BookmarkTarget): boolean {
  const m = marks.value;
  return m?.space === space && m.keys.has(bookmarkKey(b));
}

/** Marks `b` (un)bookmarked in what's shown, at once. */
function mark(space: string, b: BookmarkTarget, on: boolean): void {
  const m = marks.value;
  const keys = new Set(m?.space === space ? m.keys : []);
  if (on) {
    keys.add(bookmarkKey(b));
  } else {
    keys.delete(bookmarkKey(b));
  }
  marks.value = { space, keys };
}

/** What names a bookmark (a listed one says more: its title, when it was made; a comment's, where it is). */
const NAMES = ["kind", "item", "rule", "entity", "commit"] as const;

/** The API's parameters for `b`: its kind and the id it names (a comment's id alone). */
export const bookmarkParams = (b: BookmarkTarget): Record<string, string> =>
  b.kind === "comment"
    ? { kind: "comment", comment: b.comment ?? "" }
    : Object.fromEntries(NAMES.flatMap((k) => (typeof b[k] === "string" ? [[k, b[k]]] : [])));

/**
 * Bookmarks `b` in `space`, or removes the bookmark (`on: false`): shown at once, said in a toast, put back as it was
 * if the server refuses. Resolves once it's saved, to whether it was.
 */
export async function setBookmark(space: string, b: BookmarkTarget, on: boolean): Promise<boolean> {
  mark(space, b, on);
  saving += 1;
  try {
    if (on) {
      await api.post(spaceApi({ id: space }, "/bookmarks"), bookmarkParams(b));
    } else {
      await api.del(spaceApi({ id: space }, "/bookmarks"), { query: bookmarkParams(b) });
    }
  } catch (e) {
    mark(space, b, !on);
    toast(asApiError(e).message, "error");
    return false;
  } finally {
    // What was loaded meanwhile may be from before it: loaded again, from after it.
    saving -= 1;
    toggled.value += 1;
  }
  toast(t(on ? "bookmarks.added" : "bookmarks.removed"));
  return true;
}
